import { useState } from 'react';
import { Icono } from '@xiltrion/ui/icono';
import { Field } from '@/components/ui';
import { Button } from '@xiltrion/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@xiltrion/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@xiltrion/ui/dropdown-menu';
import { Input } from '@xiltrion/ui/input';
import { ConfirmarDialogo } from '@xiltrion/ui/bloques/confirmar';
import { useSession } from '@/app/session';
import { useLocalStorage } from './lib';
import type { SavedView } from './inboxConfig';

/** Saved views: stored per browser; coordinators share by link (query params carry the filters). */
export function SavedViewsMenu({ currentParams, currentColumns, onApply, notify }: { currentParams: string; currentColumns: string[]; onApply: (v: SavedView) => void; notify: (severity: 'success' | 'info', summary: string, detail?: string) => void }) {
  const { admin, supervisor } = useSession();
  const lead = admin || supervisor;
  const [views, setViews] = useLocalStorage<SavedView[]>('pqrs.inbox.views', []);
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState('');
  const [toDelete, setToDelete] = useState<SavedView | null>(null);
  const share = (v: SavedView) => {
    const url = `${window.location.origin}/pqrs?${v.params}`;
    void navigator.clipboard?.writeText(url).then(() => notify('success', 'Enlace copiado', 'Compártelo con tu equipo para que vean la misma vista.'));
  };
  const save = () => {
    const v: SavedView = { id: crypto.randomUUID(), name: name.trim(), params: currentParams, columns: currentColumns, createdAt: new Date().toISOString() };
    setViews((vs) => [...vs.filter((x) => x.name !== v.name), v]); setSaveOpen(false); notify('success', `Vista "${v.name}" guardada`);
  };
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild><Button variant="outline" aria-label="Vistas guardadas"><Icono nombre='mdi:bookmark-outline' /> Vistas</Button></DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-80">
          {views.length ? views.map((v) => (
            <DropdownMenuItem key={v.id} onSelect={() => onApply(v)} className="gap-2">
              <Icono nombre='mdi:bookmark-outline' className="text-muted-foreground" aria-hidden />
              <span className="flex-1 truncate">{v.name}</span>
              {lead && <Button variant="ghost" size="icon-xs" aria-label={`Compartir vista ${v.name}`} onClick={(e) => { e.stopPropagation(); share(v); }}><Icono nombre='mdi:share-variant-outline' /></Button>}
              <Button variant="ghost" size="icon-xs" className="text-destructive hover:text-destructive" aria-label={`Eliminar vista ${v.name}`} onClick={(e) => { e.stopPropagation(); setToDelete(v); }}><Icono nombre='mdi:trash-can-outline' /></Button>
            </DropdownMenuItem>
          )) : <DropdownMenuItem disabled>Aún no tienes vistas guardadas</DropdownMenuItem>}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => { setName(''); setSaveOpen(true); }}><Icono nombre='mdi:content-save-outline' /> Guardar vista actual…</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ConfirmarDialogo abierto={!!toDelete} onAbiertoChange={(o) => { if (!o) setToDelete(null); }} titulo="Eliminar vista" descripcion={toDelete ? `¿Eliminar la vista "${toDelete.name}"?` : undefined} textoConfirmar="Eliminar" destructivo onConfirmar={() => { if (toDelete) setViews((vs) => vs.filter((x) => x.id !== toDelete.id)); }} />
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader><DialogTitle>Guardar vista</DialogTitle></DialogHeader>
          <Field label="Nombre de la vista" htmlFor="view-name" hint="Guarda los filtros, el orden y las columnas visibles actuales.">
            <Input id="view-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej. Mis vencidas, Salud sin asignar" autoFocus onKeyDown={(e) => { if (e.key === 'Enter' && name.trim().length >= 2) { (e.target as HTMLInputElement).blur(); } }} />
          </Field>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSaveOpen(false)}>Cancelar</Button>
            <Button disabled={name.trim().length < 2} onClick={save}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
